import type { Result } from '../generated/orval/schemas/result';
import type { TeamSummary } from '../generated/orval/schemas/teamSummary';
import type { TournamentGroupSummary } from '../generated/orval/schemas/tournamentGroupSummary';
import { ClassFormatEnum } from '../types/enums';
import {
  Group,
  MatchResult,
  Team,
  TournamentCategory,
  TournamentGroup,
} from '../types/tournament';

/** Group on a match: OpenAPI summary, play-form group, or minimal stubs (tests). */
export type BoxLeagueElimTournamentGroupLike =
  | TournamentGroupSummary
  | TournamentGroup
  | (Pick<TournamentGroupSummary, 'id'> & Partial<Omit<TournamentGroupSummary, 'id'>>);

/** Team presence on a match: DTO summary, domain team, or minimal stubs (tests). */
export type BoxLeagueElimTeamLike =
  | TeamSummary
  | Team
  | (Pick<TeamSummary, 'id'> & Partial<Omit<TeamSummary, 'id'>>);

/** Result row: DTO / domain row, or minimal stub (helpers only use `results.length`). */
export type BoxLeagueElimResultLike = Result | MatchResult | Readonly<{ id?: number | string }>;

/**
 * Fields used by box-league elimination play helpers.
 * Aligns with {@link import('../generated/orval/schemas/tournamentMatchWithClass').TournamentMatchWithClass}
 * but allows partial placeholders / group stubs (same shapes as play data and unit tests).
 */
export type BoxLeagueElimMatchLike = {
  tournamentGroup?: BoxLeagueElimTournamentGroupLike | null;
  homeTeamPlaceholder?: string | null;
  awayTeamPlaceholder?: string | null;
  tournamentCategory?: Pick<TournamentCategory, 'roundNumber'> | null;
  results?: ReadonlyArray<BoxLeagueElimResultLike>;
  homeTeam?: BoxLeagueElimTeamLike | null;
  awayTeam?: BoxLeagueElimTeamLike | null;
};

function isBoxLeagueFormat(format: number | undefined): boolean {
  return (
    format === ClassFormatEnum.BOX_LEAGUE ||
    format === ClassFormatEnum.BOX_LEAGUE_ELIM
  );
}

export function groupsHaveBoxLeagueRoundMeta(groups: Group[]): boolean {
  return groups.some((g) => g.boxLeagueRoundNumber != null);
}

/** Stable order for Play tab: ladder round, box index within round, then id. */
export function sortBoxLeagueGroupsForPlayDisplay(groups: Group[]): Group[] {
  return [...groups].sort((a, b) => {
    const ra = a.boxLeagueRoundNumber != null ? a.boxLeagueRoundNumber : 1;
    const rb = b.boxLeagueRoundNumber != null ? b.boxLeagueRoundNumber : 1;
    if (ra !== rb) return ra - rb;
    const ia = a.boxIndex != null ? a.boxIndex : 0;
    const ib = b.boxIndex != null ? b.boxIndex : 0;
    if (ia !== ib) return ia - ib;
    const ida = typeof a.id === 'number' ? a.id : Number.MAX_SAFE_INTEGER;
    const idb = typeof b.id === 'number' ? b.id : Number.MAX_SAFE_INTEGER;
    return ida - idb;
  });
}

/**
 * All box ladder groups for the Play form when the class is box league and
 * uses moveBox or per-round group rows. Previously only the latest round was
 * shown; we return every group so boxes and matches render together.
 */
export function getFormTournamentGroupsForPlay(classData: {
  format?: number;
  moveBox?: boolean;
  tournamentGroups?: Group[];
}): Group[] {
  const raw = classData?.tournamentGroups;
  if (!raw?.length) return [];

  if (
    !isBoxLeagueFormat(classData.format) ||
    (!classData.moveBox && !groupsHaveBoxLeagueRoundMeta(raw))
  ) {
    return raw;
  }

  return sortBoxLeagueGroupsForPlayDisplay(raw);
}

export function countUniquePlayersInPlayGroup(group: Group): number {
  const ids = new Set<number>();
  group.teams?.forEach((team: { users?: Array<{ id?: number } | number> } | null) => {
    if (!team) return;
    team.users?.forEach((u) => {
      const id = typeof u === 'number' ? u : u?.id;
      if (typeof id === 'number') ids.add(id);
    });
  });
  return ids.size;
}

type BoxLeagueGenerateReadyOptions = {
  /** When true, only round-1 ladder groups are validated (same as when groups include round metadata). */
  moveBox?: boolean;
};

/**
 * Mirrors server rules: 4–10 distinct players per box before generating matches.
 * With moveBox or per-round group rows, only round-1 boxes are checked; at least one must have 4–10 players.
 */
export function boxLeaguePlayGroupsReadyForGenerate(
  groups: Group[],
  options?: BoxLeagueGenerateReadyOptions,
): boolean {
  if (!groups.length) return false;

  const hasMultiRoundMeta = groupsHaveBoxLeagueRoundMeta(groups);
  const roundOneOnly = Boolean(options?.moveBox) || hasMultiRoundMeta;

  const toCheck = roundOneOnly
    ? groups.filter((g) => {
        const r = g.boxLeagueRoundNumber;
        return r == null || r === 1;
      })
    : groups;

  if (!toCheck.length) return false;

  if (roundOneOnly) {
    let hasPlayable = false;
    for (const g of toCheck) {
      const n = countUniquePlayersInPlayGroup(g);
      if (n === 0) continue;
      if (n < 4 || n > 10) return false;
      hasPlayable = true;
    }
    return hasPlayable;
  }

  return toCheck.every((g) => {
    const n = countUniquePlayersInPlayGroup(g);
    return n >= 4 && n <= 10;
  });
}

/** Category round after configured ladder depth (progression / final box round). */
export function isBoxLeagueElimProgressionRoundMatch(
  match: Pick<BoxLeagueElimMatchLike, 'tournamentCategory'>,
  boxLeagueNumOfRounds: number,
): boolean {
  const r = match.tournamentCategory?.roundNumber;
  if (typeof r !== 'number') return false;
  return r > boxLeagueNumOfRounds;
}

/**
 * True when every ladder (non-progression) match that belongs to a box group has at least one result.
 * Progression-round matches are ignored — they are placeholders until assign / play.
 */
export function boxLeagueElimLadderGroupMatchesComplete(
  matches: BoxLeagueElimMatchLike[],
  boxLeagueNumOfRounds: number,
): boolean {
  const ladder = matches.filter(
    (m) =>
      Boolean(m.tournamentGroup) &&
      !isBoxLeagueElimProgressionRoundMatch(m, boxLeagueNumOfRounds),
  );
  if (!ladder.length) return false;
  return !ladder.some((m) => !m.results?.length);
}

/** Final / progression round still has TBD placeholders or no teams yet. */
export function boxLeagueElimProgressionRoundNeedsAssign(
  matches: BoxLeagueElimMatchLike[],
  boxLeagueNumOfRounds: number,
): boolean {
  return matches.some((m) => {
    if (!isBoxLeagueElimProgressionRoundMatch(m, boxLeagueNumOfRounds)) {
      return false;
    }
    if (m.homeTeamPlaceholder === 'TBD' || m.awayTeamPlaceholder === 'TBD') {
      return true;
    }
    return !m.homeTeam && !m.awayTeam;
  });
}

/**
 * "Assign final boxes" (generate progression) should show: ladder fully scored, and either
 * progression placeholders exist, or legacy elimination rows (no tournamentGroup) await teams.
 */
export function boxLeagueElimAssignFinalButtonVisible(
  matches: BoxLeagueElimMatchLike[],
  boxLeagueNumOfRounds: number,
): boolean {
  if (!matches.length) return false;
  const rounds = boxLeagueNumOfRounds || 1;
  if (!boxLeagueElimLadderGroupMatchesComplete(matches, rounds)) {
    return false;
  }
  if (boxLeagueElimProgressionRoundNeedsAssign(matches, rounds)) {
    return true;
  }
  const hasLegacyElimSlots = matches.some((m) => !m.tournamentGroup);
  const legacyAllUnassigned = !matches.some(
    (m) => !m.tournamentGroup && (m.homeTeam || m.awayTeam),
  );
  return hasLegacyElimSlots && legacyAllUnassigned;
}
